"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeft,
  DollarSign,
  Building2,
  Clock,
  Plus,
  Link2,
  Unlink,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckSquare,
  Square,
  Search,
  X,
  UserPlus,
  ExternalLink,
  Copy,
  Check,
  ChevronRight,
  Upload,
  Eye,
  EyeOff,
  TrendingUp,
  RefreshCw,
  Wallet,
  CheckCircle2,
} from "lucide-react";
import {
  PageWrapper,
  PageHeader,
  StatCard,
  SectionCard,
  Button,
  TabBar,
  StatusBadge,
  ProgressBar,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { formatCurrency, truncateHash, formatDateTime, formatDateCompact, timeAgo, copyText, NIGERIAN_BANKS } from "@/lib/utils";
import { treasuryAccounts, monoBankStatements, recentTrades, counterparties, type Trade, type Counterparty, hydrateBankTransactions, hydrateTreasuryAccounts } from "@/lib/mock-data";
import type { DetectedTransaction } from "@/lib/scanner/types";
import { useToast } from "@/contexts/toast-context";

// --- Types matching the API responses ---

type LockRecord = {
  id: number;
  reference?: string;
  lockType: "lock_in" | "lock_out";
  parentLockId: number | null;
  counterpartyAddress: string;
  counterpartyName: string | null;
  usdtAmount: number;
  rate: number | null;
  ngnAmount: number | null;
  expiryAt: string | null;
  nairaFunded: number;
  usdtSourced: number;
  usdtDelivered: number;
  nairaPaid: number;
  usdtReceived: number;
  status: string;
  notes: string | null;
  shareToken?: string | null;
  counterpartyEmail?: string | null;
  counterpartyPhone?: string | null;
  mirrorLockId?: number | null;
  balanceSnapshot?: { chain: string; address: string; balance: number }[] | null;
  cancelRequestedBy?: string | null;
  cancelRequestedAt?: string | null;
  rateRequestedBy?: string | null;
  rateRequestedAt?: string | null;
  rateRequestedValue?: number | null;
  createdAt: string;
  updatedAt: string;
  entryCount?: number;
  childCount?: number;
};

type LockEntry = {
  id: number;
  lockId: number;
  entryType: string;
  transactionId: string | null;
  tradeId: number | null;
  amount: number;
  currency: string;
  method: string | null;
  reference: string | null;
  sourceLabel: string | null;
  paidToName: string | null;
  paidToPhone: string | null;
  createdAt: string;
};

// --- Animation variants ---
const listItem = {
  hidden: { opacity: 0, x: -6 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] as const } },
};

export default function LocksPage() {
  const [locks, setLocks] = useState<LockRecord[]>([]);
  const [tab, setTab] = useState("All");
  const [selectedLock, setSelectedLock] = useState<LockRecord | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<LockEntry[]>([]);
  const [selectedChildren, setSelectedChildren] = useState<LockRecord[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState<"lock_in" | "lock_out">("lock_in");
  const [createParentId, setCreateParentId] = useState<number | null>(null);

  const loadLocks = useCallback(async () => {
    const res = await fetch("/api/locks");
    const data = await res.json();
    setLocks(data.locks || []);
  }, []);

  useEffect(() => {
    loadLocks();
    hydrateBankTransactions();
    hydrateTreasuryAccounts();
  }, [loadLocks]);

  const openDetail = async (lock: LockRecord) => {
    const res = await fetch(`/api/locks/${lock.id}`);
    const data = await res.json();
    setSelectedLock(data.lock);
    setSelectedEntries(data.entries || []);
    setSelectedChildren(data.children || []);
  };

  const openCreate = (type: "lock_in" | "lock_out", parentId: number | null = null) => {
    setCreateType(type);
    setCreateParentId(parentId);
    setShowCreate(true);
  };

  // Stats
  const activeLockIns = locks.filter((l) => l.lockType === "lock_in" && !["cancelled", "expired"].includes(l.status));
  const activeLockOuts = locks.filter((l) => l.lockType === "lock_out" && !["cancelled", "expired"].includes(l.status));
  const totalLocked = activeLockIns.reduce((sum, l) => sum + l.usdtAmount, 0);
  const expiringSoon = locks.filter((l) => {
    if (!l.expiryAt || l.status === "cancelled" || l.status === "filled") return false;
    const diff = new Date(l.expiryAt).getTime() - Date.now();
    return diff > 0 && diff < 48 * 60 * 60 * 1000;
  });

  const filteredLocks = tab === "All" ? locks : tab === "Lock INs" ? locks.filter((l) => l.lockType === "lock_in") : locks.filter((l) => l.lockType === "lock_out");

  // Group locks: Lock IN + its child Lock OUTs form a group, standalone locks are solo groups
  const lockGroups = (() => {
    const groups: LockRecord[][] = [];
    const childIds = new Set<number>();
    const filteredIds = new Set(filteredLocks.map((l) => l.id));

    // First pass: collect all Lock OUTs that have a parent (only if parent is in filtered list)
    const childrenByParent = new Map<number, LockRecord[]>();
    for (const l of filteredLocks) {
      if (l.parentLockId != null && filteredIds.has(l.parentLockId)) {
        const arr = childrenByParent.get(l.parentLockId) || [];
        arr.push(l);
        childrenByParent.set(l.parentLockId, arr);
        childIds.add(l.id);
      }
    }

    // Second pass: build groups in list order
    for (const l of filteredLocks) {
      if (childIds.has(l.id)) continue; // will be included under its parent
      const group = [l];
      const children = childrenByParent.get(l.id);
      if (children) group.push(...children);
      groups.push(group);
    }

    return groups;
  })();

  return (
    <PageWrapper>
      <PageHeader
        title="Locks"
        subtitle="Manage Lock IN and Lock OUT lifecycle"
        actions={
          <>
            <Button variant="secondary" onClick={() => openCreate("lock_out")}>
              <Plus className="w-3.5 h-3.5" /> Lock OUT
            </Button>
            <Button onClick={() => openCreate("lock_in")}>
              <Plus className="w-3.5 h-3.5" /> Lock IN
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard label="Active Lock INs" value={String(activeLockIns.length)} delay={0} />
        <StatCard label="Active Lock OUTs" value={String(activeLockOuts.length)} delay={0.05} />
        <StatCard label="Total Locked" value={formatCurrency(totalLocked)} delay={0.1} />
        <StatCard
          label="Expiring Soon"
          value={String(expiringSoon.length)}
          subValue={expiringSoon.length > 0 ? "within 48h" : undefined}
          trend={expiringSoon.length > 0 ? "down" : "neutral"}
          delay={0.15}
        />
      </div>

      {/* Tabs + List */}
      <SectionCard>
        <div className="px-4 pt-3">
          <TabBar
            tabs={[
              { label: "All", count: locks.length },
              { label: "Lock INs", count: locks.filter((l) => l.lockType === "lock_in").length },
              { label: "Lock OUTs", count: locks.filter((l) => l.lockType === "lock_out").length },
            ]}
            active={tab}
            onChange={setTab}
          />
        </div>
        <div className="p-3 flex flex-col gap-4">
          {filteredLocks.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Lock className="w-8 h-8 text-muted mx-auto mb-2" />
              <p className="text-[13px] text-muted">No locks found</p>
            </div>
          ) : (
            lockGroups.map((group) => (
              <div key={group[0].id}>
                <p className="text-[10px] text-muted uppercase tracking-wider mb-1.5 px-1">
                  {formatDateCompact(group[0].createdAt)}
                  {group.length > 1 && ` · ${group.length} locks`}
                </p>
                <div
                  className="rounded-lg overflow-hidden"
                  style={{ border: "1px solid var(--border)" }}
                >
                {group.map((lock, j) => {
                  const isIn = lock.lockType === "lock_in";
                  const progress = isIn
                    ? lock.usdtAmount > 0
                      ? Math.min(((lock.usdtSourced + lock.usdtDelivered) / (lock.usdtAmount * 2)) * 100, 100)
                      : 0
                    : lock.usdtAmount > 0
                    ? Math.min((lock.usdtReceived / lock.usdtAmount) * 100, 100)
                    : 0;

                  const isExpiring =
                    lock.expiryAt &&
                    !["cancelled", "filled"].includes(lock.status) &&
                    new Date(lock.expiryAt).getTime() - Date.now() < 48 * 60 * 60 * 1000 &&
                    new Date(lock.expiryAt).getTime() > Date.now();

                  return (
                    <motion.div
                      key={lock.id}
                      variants={listItem}
                      initial="hidden"
                      animate="visible"
                      onClick={() => openDetail(lock)}
                      className="px-4 py-3.5 cursor-pointer hover:bg-surface-2/50 transition-colors"
                      style={{
                        borderLeft: `3px solid var(${isIn ? "--accent" : "--warning"})`,
                        borderTop: j > 0 ? "1px solid var(--border)" : undefined,
                      }}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${isIn ? "text-accent" : "text-warning"}`}>
                            {isIn ? "LOCK IN" : "LOCK OUT"}
                          </span>
                          {lock.reference && (
                            <span className="text-[10px] text-muted font-mono">{lock.reference}</span>
                          )}
                          <StatusBadge status={lock.status} />
                          {isExpiring && (
                            <span className="text-[10px] font-semibold text-warning flex items-center gap-0.5">
                              <Clock className="w-3 h-3" /> EXPIRING
                            </span>
                          )}
                          {lock.mirrorLockId && (
                            <span className="text-[10px] font-semibold text-accent flex items-center gap-0.5">
                              <Link2 className="w-3 h-3" /> MIRRORED
                            </span>
                          )}
                          {lock.parentLockId && (
                            <span className="text-[10px] text-muted">parent #{lock.parentLockId}</span>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-foreground tabular-nums">
                          {formatCurrency(lock.usdtAmount)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[13px] text-foreground">
                          {lock.counterpartyName || truncateHash(lock.counterpartyAddress)}
                        </span>
                        {lock.rate && (
                          <span className="text-[11px] text-muted tabular-nums">
                            {formatCurrency(lock.rate, "NGN")}/USDT
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <ProgressBar percentage={progress} />
                        </div>
                        <span className="text-[10px] text-muted whitespace-nowrap tabular-nums">
                          {isIn
                            ? `S:${formatCurrency(lock.usdtSourced)} D:${formatCurrency(lock.usdtDelivered)}`
                            : `R:${formatCurrency(lock.usdtReceived)}`}
                          {" / "}
                          {formatCurrency(lock.usdtAmount)}
                        </span>
                      </div>
                      {lock.expiryAt && (
                        <p className="text-[10px] text-muted mt-1">Expires: {formatDateTime(lock.expiryAt)}</p>
                      )}
                    </motion.div>
                  );
                })}
                </div>
              </div>
            ))
          )}
        </div>
      </SectionCard>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedLock && (
          <LockDetailModal
            lock={selectedLock}
            entries={selectedEntries}
            children={selectedChildren}
            parentLock={selectedLock.parentLockId ? locks.find(l => l.id === selectedLock.parentLockId) || null : null}
            onClose={() => setSelectedLock(null)}
            onUpdated={() => {
              loadLocks();
              if (selectedLock) openDetail(selectedLock);
            }}
            onCreateLockOut={(parentId) => {
              setSelectedLock(null);
              openCreate("lock_out", parentId);
            }}
          />
        )}
      </AnimatePresence>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreate && (
          <CreateLockModal
            lockType={createType}
            parentLockId={createParentId}
            locks={locks.filter((l) => l.lockType === "lock_in")}
            onClose={() => setShowCreate(false)}
            onCreated={() => {
              setShowCreate(false);
              loadLocks();
            }}
          />
        )}
      </AnimatePresence>
    </PageWrapper>
  );
}

// =============================================================================
// Lock Detail Modal
// =============================================================================

function LockDetailModal({
  lock,
  entries,
  children,
  parentLock,
  onClose,
  onUpdated,
  onCreateLockOut,
}: {
  lock: LockRecord;
  entries: LockEntry[];
  children: LockRecord[];
  parentLock?: LockRecord | null;
  onClose: () => void;
  onUpdated: () => void;
  onCreateLockOut: (parentId: number) => void;
}) {
  const [showSection, setShowSection] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState("");
  const [livelock, setLiveLock] = useState(lock);
  useEffect(() => { setLiveLock(lock); }, [lock]);
  const { toast } = useToast();

  // Poll for updates when Lock OUT has a pending rate request
  useEffect(() => {
    if (livelock.lockType === "lock_in" || !livelock.rateRequestedValue) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/locks/${livelock.id}`);
        const data = await res.json();
        if (data.lock && !data.lock.rateRequestedValue && livelock.rateRequestedValue) {
          // Request was resolved by counterparty
          const rateChanged = data.lock.rate !== livelock.rate;
          toast(rateChanged ? "Rate change accepted by counterparty" : "Rate change declined by counterparty", rateChanged ? "success" : "info");
          onUpdated();
        }
        setLiveLock(data.lock);
      } catch {
        // ignore polling errors
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [livelock.id, livelock.lockType, livelock.rateRequestedValue, livelock.rate, onUpdated, toast]);

  const handleStatusChange = async (status: string) => {
    const payload: Record<string, string> = { status };
    if (status === "cancel_requested") {
      payload.cancelRequestedBy = "Jeff Trading";
    }
    await fetch(`/api/locks/${lock.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    onUpdated();
  };

  const handleRateUpdate = async (newRate: number) => {
    await fetch(`/api/locks/${lock.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rate: newRate }),
    });
    setEditingRate(false);
    toast(`Rate updated to ₦${newRate.toLocaleString()}`, "success");
    onUpdated();
  };

  const handleRateRequest = async (newRate: number) => {
    await fetch(`/api/locks/${lock.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rateRequest: newRate }),
    });
    setEditingRate(false);
    toast("Rate change requested", "success");
    onUpdated();
  };

  const handleRateResponse = async (response: "accept" | "decline") => {
    await fetch(`/api/locks/${lock.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rateResponse: response }),
    });
    toast(response === "accept" ? "Rate updated" : "Rate change declined", "success");
    onUpdated();
  };

  const handleCancelRateRequest = async () => {
    await fetch(`/api/locks/${lock.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rateResponse: "decline" }),
    });
    toast("Rate change request cancelled", "success");
    onUpdated();
  };

  const handleLinkTransactions = async (entryType: string, transactionIds: string[]) => {
    const res = await fetch(`/api/locks/${lock.id}/entries`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entryType, transactionIds }),
    });
    if (res.ok) {
      setShowSection(null);
      onUpdated();
    }
  };

  const handleAddNairaEntry = async (
    entryType: string,
    data: { amount: number; method: string; reference: string; sourceLabel: string }
  ) => {
    const res = await fetch(`/api/locks/${lock.id}/entries`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entryType, ...data }),
    });
    if (res.ok) {
      setShowSection(null);
      onUpdated();
    }
  };

  const handleAddNairaBankStatements = async (
    entryType: string,
    entries: { amount: number; reference: string; sourceLabel: string }[]
  ) => {
    for (const entry of entries) {
      await fetch(`/api/locks/${lock.id}/entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entryType,
          amount: entry.amount,
          method: "bank_transfer",
          reference: entry.reference,
          sourceLabel: entry.sourceLabel,
        }),
      });
    }
    setShowSection(null);
    onUpdated();
  };

  const handleLinkTradeIds = async (entryType: string, tradeIds: number[]) => {
    const res = await fetch(`/api/locks/${lock.id}/entries`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entryType, tradeIds }),
    });
    if (res.ok) {
      setShowSection(null);
      onUpdated();
    }
  };

  const handleUnlink = async (entryId: number) => {
    await fetch(`/api/locks/${lock.id}/entries?entryId=${entryId}`, { method: "DELETE" });
    onUpdated();
  };

  const entriesByType = (type: string) => entries.filter((e) => e.entryType === type);
  const isIn = lock.lockType === "lock_in";

  return (
    <ModalOverlay onClose={onClose} title={`${isIn ? "Lock IN" : "Lock OUT"} #${lock.id}`}>
      <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-bold uppercase tracking-wider ${isIn ? "text-accent" : "text-warning"}`}>
                {isIn ? "LOCK IN" : "LOCK OUT"}
              </span>
              {lock.reference && (
                <span className="text-[10px] text-muted font-mono">{lock.reference}</span>
              )}
              <StatusBadge status={lock.status} />
            </div>
            <p className="text-[13px] text-foreground font-medium">
              {lock.counterpartyName || truncateHash(lock.counterpartyAddress)}
            </p>
            <p className="text-[11px] text-muted mt-0.5">
              {formatCurrency(lock.usdtAmount)} USDT
              {lock.rate && !editingRate && (
                <>
                  {" @ "}
                  {!isIn && livelock.rateRequestedValue ? (
                    <span className="inline-flex items-center gap-0.5 text-foreground font-medium">
                      {formatCurrency(lock.rate, "NGN")}
                    </span>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setRateInput(String(lock.rate));
                        setEditingRate(true);
                      }}
                      className="inline-flex items-center gap-0.5 text-foreground font-medium hover:text-accent transition-colors cursor-pointer"
                      title="Adjust rate"
                    >
                      {formatCurrency(lock.rate, "NGN")}
                      <TrendingUp className="w-3 h-3 inline" />
                    </button>
                  )}
                </>
              )}
              {!lock.rate && !editingRate && !(!isIn && livelock.rateRequestedValue) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRateInput("");
                    setEditingRate(true);
                  }}
                  className="ml-1 text-[10px] text-accent hover:underline cursor-pointer"
                >
                  + set rate
                </button>
              )}
              {lock.expiryAt && ` · Expires ${formatDateTime(lock.expiryAt)}`}
            </p>
            {editingRate && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <button
                  onClick={() => {
                    const current = parseFloat(rateInput) || 0;
                    if (current > 0) setRateInput(String(current - 5));
                  }}
                  className="w-6 h-6 rounded bg-surface-2 text-foreground text-[13px] font-bold flex items-center justify-center hover:bg-surface-3 cursor-pointer"
                >
                  −
                </button>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-muted">₦</span>
                  <input
                    autoFocus
                    type="text"
                    inputMode="numeric"
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value.replace(/[^0-9.]/g, ""))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const v = parseFloat(rateInput);
                        if (v > 0) {
                          if (isIn) {
                            handleRateUpdate(v);
                          } else {
                            handleRateRequest(v);
                          }
                        }
                      }
                      if (e.key === "Escape") setEditingRate(false);
                    }}
                    className="w-28 bg-surface border border-border rounded px-2 pl-5 py-1 text-[12px] text-foreground tabular-nums"
                    placeholder="e.g. 1650"
                  />
                </div>
                <button
                  onClick={() => {
                    const current = parseFloat(rateInput) || 0;
                    setRateInput(String(current + 5));
                  }}
                  className="w-6 h-6 rounded bg-surface-2 text-foreground text-[13px] font-bold flex items-center justify-center hover:bg-surface-3 cursor-pointer"
                >
                  +
                </button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    const v = parseFloat(rateInput);
                    if (v > 0) {
                      if (isIn) {
                        handleRateUpdate(v);
                      } else {
                        handleRateRequest(v);
                      }
                    }
                  }}
                  disabled={!parseFloat(rateInput)}
                >
                  {isIn ? "Update" : "Request"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditingRate(false)}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
            {lock.parentLockId && parentLock && (
              <div className="mt-1 px-2 py-1.5 rounded-md bg-accent/5" style={{ border: "1px solid var(--accent-border, rgba(59,130,246,0.2))" }}>
                <p className="text-[11px] text-accent font-semibold">
                  Parent Lock IN #{lock.parentLockId}
                </p>
                <p className="text-[10px] text-muted">
                  {parentLock.counterpartyName || truncateHash(parentLock.counterpartyAddress)} — {formatCurrency(parentLock.usdtAmount)} USDT
                  {parentLock.rate && ` @ ${formatCurrency(parentLock.rate, "NGN")}`}
                </p>
                <div className="mt-1">
                  <ProgressBar percentage={parentLock.usdtAmount > 0 ? Math.min(((parentLock.usdtSourced + parentLock.usdtDelivered) / (parentLock.usdtAmount * 2)) * 100, 100) : 0} />
                </div>
              </div>
            )}
            {lock.parentLockId && !parentLock && (
              <p className="text-[11px] text-muted">Parent Lock IN: #{lock.parentLockId}</p>
            )}
          </div>
          {lock.status === "cancel_requested" ? (
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-warning">
              <AlertTriangle className="w-3.5 h-3.5" /> Cancel Requested
            </span>
          ) : lock.status !== "filled" && lock.status !== "cancelled" ? (
            <Button variant="danger" size="sm" onClick={() => setShowCancelConfirm(true)}>
              Cancel
            </Button>
          ) : null}
        </div>

        {/* Cancel requested banner */}
        {lock.status === "cancel_requested" && (
          <div className="px-3 py-2.5 rounded-md bg-warning/10" style={{ border: "1px solid rgba(234,179,8,0.3)" }}>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-warning" />
              <div>
                <p className="text-[12px] font-semibold text-warning">Cancellation Requested</p>
                <p className="text-[10px] text-muted">
                  {lock.cancelRequestedBy && `Requested by ${lock.cancelRequestedBy}`}
                  {lock.cancelRequestedAt && ` · ${timeAgo(lock.cancelRequestedAt)}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rate change request banner */}
        {livelock.rateRequestedValue && (
          isIn ? (
            /* Lock IN owner sees accept/decline */
            <div className="px-3 py-2.5 rounded-md bg-accent/10" style={{ border: "1px solid rgba(59,130,246,0.3)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-accent" />
                  <div>
                    <p className="text-[12px] font-semibold text-accent">
                      {livelock.rateRequestedBy || "Counterparty"} requested rate change to ₦{livelock.rateRequestedValue.toLocaleString()}
                    </p>
                    {livelock.rateRequestedAt && (
                      <p className="text-[10px] text-muted">{timeAgo(livelock.rateRequestedAt)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="primary" size="sm" onClick={() => handleRateResponse("accept")}>
                    Accept
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => handleRateResponse("decline")}>
                    Decline
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* Lock OUT owner sees pending status */
            <div className="px-3 py-2.5 rounded-md bg-warning/10" style={{ border: "1px solid rgba(234,179,8,0.3)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-warning" />
                  <div>
                    <p className="text-[12px] font-semibold text-warning">
                      You requested rate change to ₦{livelock.rateRequestedValue.toLocaleString()} · Pending
                    </p>
                    {livelock.rateRequestedAt && (
                      <p className="text-[10px] text-muted">{timeAgo(livelock.rateRequestedAt)}</p>
                    )}
                  </div>
                </div>
                <Button variant="secondary" size="sm" onClick={handleCancelRateRequest}>
                  Cancel Request
                </Button>
              </div>
            </div>
          )
        )}

        {/* Mirror info card */}
        {lock.mirrorLockId && (
          <div className="px-3 py-2.5 rounded-md bg-accent/5" style={{ border: "1px solid rgba(59,130,246,0.2)" }}>
            <div className="flex items-center gap-2">
              <Link2 className="w-3.5 h-3.5 text-accent" />
              <span className="text-[11px] font-semibold text-accent">
                {isIn ? "Mirrored from" : "Mirrored to"} Lock #{lock.mirrorLockId}
              </span>
            </div>
          </div>
        )}

        {/* Guest Link card for lock_out with shareToken */}
        {!isIn && lock.shareToken && (
          <GuestLinkCard shareToken={lock.shareToken} counterpartyEmail={lock.counterpartyEmail} />
        )}

        {/* Wallet Tracker for Lock OUT — between Guest Link and entry sections */}
        {!isIn && lock.status !== "cancelled" && lock.status !== "expired" && lock.status !== "cancel_requested" && (
          <WalletTracker lockId={lock.id} usdtAmount={lock.usdtAmount} counterpartyName={lock.counterpartyName} />
        )}

        {isIn ? (
          <>
            <EntrySection
              title="Naira Funding"
              entries={entriesByType("naira_funding")}
              currency="NGN"
              progress={lock.ngnAmount ? { filled: lock.nairaFunded, target: lock.ngnAmount } : undefined}
              showForm={showSection === "naira_funding"}
              onToggle={() => setShowSection(showSection === "naira_funding" ? null : "naira_funding")}
              formType="naira"
              onSubmitNaira={(data) => handleAddNairaEntry("naira_funding", data)}
              onSubmitNairaBankStatements={(entries) => handleAddNairaBankStatements("naira_funding", entries)}
              onUnlink={handleUnlink}
            />
            <EntrySection
              title="USDT Sourced"
              entries={entriesByType("usdt_sourced")}
              currency="USDT"
              progress={{ filled: lock.usdtSourced, target: lock.usdtAmount }}
              showForm={showSection === "usdt_sourced"}
              onToggle={() => setShowSection(showSection === "usdt_sourced" ? null : "usdt_sourced")}
              formType="tx"
              direction="in"
              targetAmount={lock.usdtAmount}
              alreadyFilled={lock.usdtSourced}
              onSubmitTx={(ids) => handleLinkTransactions("usdt_sourced", ids)}
              onSubmitTradeIds={(ids) => handleLinkTradeIds("usdt_sourced", ids)}
              onUnlink={handleUnlink}
            />
            <EntrySection
              title="USDT Delivered"
              entries={entriesByType("usdt_delivered")}
              currency="USDT"
              progress={{ filled: lock.usdtDelivered, target: lock.usdtAmount }}
              showForm={showSection === "usdt_delivered"}
              onToggle={() => setShowSection(showSection === "usdt_delivered" ? null : "usdt_delivered")}
              formType="tx"
              direction="out"
              targetAmount={lock.usdtAmount}
              alreadyFilled={lock.usdtDelivered}
              onSubmitTx={(ids) => handleLinkTransactions("usdt_delivered", ids)}
              onSubmitTradeIds={(ids) => handleLinkTradeIds("usdt_delivered", ids)}
              onUnlink={handleUnlink}
            />
            {/* Child Lock OUTs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[11px] font-semibold text-muted uppercase tracking-wider">
                  Lock OUTs ({children.length})
                </h4>
                <button
                  onClick={() => onCreateLockOut(lock.id)}
                  className="text-[12px] text-accent hover:underline cursor-pointer flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> New Lock OUT
                </button>
              </div>
              {children.length > 0 ? (
                <div className="rounded-md overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                  {children.map((child) => {
                    const cp = child.usdtAmount > 0 ? Math.min((child.usdtReceived / child.usdtAmount) * 100, 100) : 0;
                    return (
                      <div key={child.id} className="px-3 py-2.5" style={{ borderBottom: "1px solid var(--border)" }}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={child.status} />
                            <span className="text-[13px] text-foreground">
                              {child.counterpartyName || truncateHash(child.counterpartyAddress)}
                            </span>
                          </div>
                          <span className="text-[13px] font-semibold text-foreground">{formatCurrency(child.usdtAmount)}</span>
                        </div>
                        <ProgressBar percentage={cp} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[12px] text-muted">No Lock OUTs yet</p>
              )}
            </div>
          </>
        ) : (
          <>
            <EntrySection
              title="Naira Paid"
              entries={entriesByType("naira_paid")}
              currency="NGN"
              progress={lock.ngnAmount ? { filled: lock.nairaPaid, target: lock.ngnAmount } : undefined}
              showForm={showSection === "naira_paid"}
              onToggle={() => setShowSection(showSection === "naira_paid" ? null : "naira_paid")}
              formType="naira"
              onSubmitNaira={(data) => handleAddNairaEntry("naira_paid", data)}
              onSubmitNairaBankStatements={(entries) => handleAddNairaBankStatements("naira_paid", entries)}
              onUnlink={handleUnlink}
            />
            <EntrySection
              title="USDT Received"
              entries={entriesByType("usdt_received")}
              currency="USDT"
              progress={{ filled: lock.usdtReceived, target: lock.usdtAmount }}
              showForm={showSection === "usdt_received"}
              onToggle={() => setShowSection(showSection === "usdt_received" ? null : "usdt_received")}
              formType="tx"
              direction="in"
              targetAmount={lock.usdtAmount}
              alreadyFilled={lock.usdtReceived}
              onSubmitTx={(ids) => handleLinkTransactions("usdt_received", ids)}
              onSubmitTradeIds={(ids) => handleLinkTradeIds("usdt_received", ids)}
              onUnlink={handleUnlink}
            />
          </>
        )}

        {lock.notes && (
          <div className="text-[12px] text-muted pt-3" style={{ borderTop: "1px solid var(--border)" }}>
            <span className="text-foreground font-medium">Notes:</span> {lock.notes}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showCancelConfirm}
        title="Request cancellation?"
        message={`This will send a cancellation request to ${lock.counterpartyName || "the counterparty"}. They will be notified via email and Telegram.`}
        confirmLabel="Yes, request cancel"
        cancelLabel="Go back"
        variant="warning"
        onConfirm={() => {
          setShowCancelConfirm(false);
          handleStatusChange("cancel_requested");
          toast("Cancellation request sent", "success");
        }}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </ModalOverlay>
  );
}

// =============================================================================
// Guest Link Card
// =============================================================================

function GuestLinkCard({ shareToken, counterpartyEmail }: { shareToken: string; counterpartyEmail?: string | null }) {
  const [copied, setCopied] = useState(false);
  const guestUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/lock/${shareToken}`;

  const handleCopy = () => {
    copyText(guestUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-md p-3 space-y-2" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-2 mb-1">
        <ExternalLink className="w-3.5 h-3.5 text-accent" />
        <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider">Guest Link</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={guestUrl}
          className="flex-1 bg-surface border border-border rounded px-2 py-1.5 text-[11px] text-muted font-mono truncate"
        />
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] font-semibold transition-colors cursor-pointer bg-accent/10 text-accent hover:bg-accent/20"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {counterpartyEmail && (
        <p className="text-[10px] text-muted">
          Email: {counterpartyEmail} (provider not configured)
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Wallet Tracker — live counterparty USDT balance
// =============================================================================

type WalletBalanceData = {
  chain: string;
  chainLabel: string;
  address: string;
  balance: number;
  isPrimary: boolean;
  error?: string;
};

type BalanceResponse = {
  wallets: WalletBalanceData[];
  totalUsdt: number;
  snapshot: { chain: string; address: string; balance: number }[] | null;
  usdtAmount: number;
  counterpartyName: string;
};

function WalletTracker({
  lockId,
  usdtAmount,
  counterpartyName,
}: {
  lockId: number;
  usdtAmount: number;
  counterpartyName: string | null;
}) {
  const [data, setData] = useState<BalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [hidden, setHidden] = useState(true);
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedMilestones = useRef<Set<number>>(new Set());
  const { toast } = useToast();

  const togglePeek = useCallback(() => {
    if (hidden) {
      // Reveal — then auto-hide after 5 seconds
      setHidden(false);
      if (peekTimer.current) clearTimeout(peekTimer.current);
      peekTimer.current = setTimeout(() => setHidden(true), 5000);
    } else {
      // Manual hide
      if (peekTimer.current) clearTimeout(peekTimer.current);
      setHidden(true);
    }
  }, [hidden]);

  const fetchBalances = useCallback(
    async (isManual?: boolean) => {
      if (isManual) setRefreshing(true);
      try {
        const res = await fetch(`/api/locks/${lockId}/balances`);
        if (res.ok) {
          const json: BalanceResponse = await res.json();
          setData(json);
          setFetchError(false);

          // Milestone toasts
          if (usdtAmount > 0) {
            const fillPct = (json.totalUsdt / usdtAmount) * 100;
            for (const m of [25, 50, 75, 100]) {
              if (fillPct >= m && !firedMilestones.current.has(m)) {
                firedMilestones.current.add(m);
                const name = counterpartyName || "Counterparty";
                if (m === 100) {
                  toast(
                    `${name} has enough USDT to fill Lock OUT #${lockId} — request funds sent!`,
                    "success"
                  );
                } else {
                  toast(
                    `${name} is ${m}% filled — ${json.totalUsdt.toLocaleString()} / ${usdtAmount.toLocaleString()} USDT`,
                    "success"
                  );
                }
              }
            }
          }
        } else {
          setFetchError(true);
        }
      } catch {
        setFetchError(true);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [lockId, usdtAmount, counterpartyName, toast]
  );

  // Fetch on mount + auto-refresh every 30s
  useEffect(() => {
    fetchBalances();
    const interval = setInterval(() => fetchBalances(), 30000);
    return () => clearInterval(interval);
  }, [fetchBalances]);

  const name = counterpartyName || "Counterparty";

  if (loading) {
    return (
      <div
        className="rounded-md p-3 space-y-2"
        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
      >
        <div className="flex items-center gap-2">
          <Eye className="w-3.5 h-3.5 text-accent animate-pulse" />
          <span className="text-[11px] text-muted">Loading {name}&apos;s wallet balances...</span>
        </div>
      </div>
    );
  }

  if (fetchError || !data) {
    return (
      <div
        className="rounded-md p-3"
        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-3.5 h-3.5 text-muted" />
            <span className="text-[11px] text-muted">Could not load wallet balances</span>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              setFetchError(false);
              fetchBalances(true);
            }}
            className="text-[11px] text-accent hover:underline cursor-pointer"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (data.wallets.length === 0) {
    return (
      <div
        className="rounded-md p-3"
        style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
      >
        <div className="flex items-center gap-2">
          <Wallet className="w-3.5 h-3.5 text-muted" />
          <span className="text-[11px] text-muted">No wallets on file for {name}</span>
        </div>
      </div>
    );
  }

  const fillPct = usdtAmount > 0 ? Math.min((data.totalUsdt / usdtAmount) * 100, 100) : 0;
  const snapshotTotal = data.snapshot?.reduce((sum, s) => sum + s.balance, 0) ?? null;
  const totalDelta = snapshotTotal !== null ? data.totalUsdt - snapshotTotal : null;

  return (
    <div
      className="rounded-md p-3 space-y-3"
      style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={togglePeek}
          className="flex items-center gap-2 cursor-pointer group"
        >
          <Eye className={`w-3.5 h-3.5 transition-colors ${hidden ? "text-muted group-hover:text-foreground" : "text-accent"}`} />
          <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider">
            Counterparty Wallets — {name}
          </span>
        </button>
        {!hidden && (
          <button
            onClick={() => fetchBalances(true)}
            disabled={refreshing}
            className="p-1 text-muted hover:text-foreground transition-colors cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {hidden ? null : <>
      {/* Wallet rows */}
      <div className="space-y-2">
        {data.wallets.map((w) => {
          const snapshotEntry = data.snapshot?.find(
            (s) => s.chain === w.chain && s.address.toLowerCase() === w.address.toLowerCase()
          );
          const delta = snapshotEntry != null ? w.balance - snapshotEntry.balance : null;

          return (
            <div key={`${w.chain}-${w.address}`} className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] font-semibold text-foreground">{w.chainLabel}</span>
                  {w.isPrimary && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: "rgba(59,130,246,0.1)", color: "var(--accent)" }}>
                      Primary
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-muted font-mono">
                  {w.address.slice(0, 8)}...{w.address.slice(-6)}
                </p>
                {delta !== null && delta !== 0 && (
                  <div className="flex items-center gap-0.5 mt-0.5" style={{ color: delta > 0 ? "var(--accent-green, #22c55e)" : "var(--danger, #ef4444)" }}>
                    <TrendingUp className={`w-2.5 h-2.5 ${delta < 0 ? "rotate-180" : ""}`} />
                    <span className="text-[10px] font-medium">
                      {delta > 0 ? "+" : ""}
                      {delta.toLocaleString(undefined, { maximumFractionDigits: 2 })} since lock
                    </span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <span className="text-[14px] font-bold text-foreground tabular-nums">
                  {w.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
                <span className="text-[11px] text-muted ml-1">USDT</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total + Progress */}
      {usdtAmount > 0 && (
        <div>
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-muted">
              Total:{" "}
              <span className="font-semibold text-foreground">
                {data.totalUsdt.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT
              </span>
            </span>
            <span className="text-muted tabular-nums">
              {fillPct.toFixed(0)}% of {formatCurrency(usdtAmount)}
            </span>
          </div>
          <ProgressBar percentage={fillPct} />
          {totalDelta !== null && totalDelta !== 0 && (
            <div className="flex items-center gap-1 mt-1.5" style={{ color: totalDelta > 0 ? "var(--accent-green, #22c55e)" : "var(--danger, #ef4444)" }}>
              <TrendingUp className={`w-3 h-3 ${totalDelta < 0 ? "rotate-180" : ""}`} />
              <span className="text-[10px] font-medium">
                {totalDelta > 0 ? "+" : ""}
                {totalDelta.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT since lock created
              </span>
            </div>
          )}
          {fillPct >= 100 && (
            <div
              className="flex items-center gap-1.5 mt-2 px-2.5 py-2 rounded-md"
              style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)" }}
            >
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#22c55e" }} />
              <span className="text-[11px] font-semibold" style={{ color: "#22c55e" }}>
                {name} has enough USDT to fill this Lock OUT
              </span>
            </div>
          )}
        </div>
      )}

      {/* Auto-refresh indicator */}
      <div className="flex items-center gap-1.5 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
        <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
        <span className="text-[10px] text-muted">Auto-refreshing every 30s</span>
      </div>
      </>}
    </div>
  );
}

// =============================================================================
// Entry Section (reusable for each progress section)
// =============================================================================

function EntrySection({
  title,
  entries,
  currency,
  progress,
  showForm,
  onToggle,
  formType,
  direction,
  targetAmount,
  alreadyFilled,
  onSubmitNaira,
  onSubmitNairaBankStatements,
  onSubmitTx,
  onSubmitTradeIds,
  onUnlink,
}: {
  title: string;
  entries: LockEntry[];
  currency: string;
  progress?: { filled: number; target: number };
  showForm: boolean;
  onToggle: () => void;
  formType: "naira" | "tx";
  direction?: "in" | "out";
  targetAmount?: number;
  alreadyFilled?: number;
  onSubmitNaira?: (data: { amount: number; method: string; reference: string; sourceLabel: string }) => void;
  onSubmitNairaBankStatements?: (entries: { amount: number; reference: string; sourceLabel: string }[]) => void;
  onSubmitTx?: (ids: string[]) => void;
  onSubmitTradeIds?: (ids: number[]) => void;
  onUnlink: (id: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] font-semibold text-muted uppercase tracking-wider">{title}</h4>
        <button
          onClick={onToggle}
          className="text-[12px] text-accent hover:underline cursor-pointer flex items-center gap-1"
        >
          {formType === "tx" ? <Link2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          {formType === "tx" ? "Add Trades" : "Add Entry"}
          {showForm ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {progress && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-muted">{title}</span>
            <span className="text-foreground tabular-nums">
              {currency === "NGN" ? formatCurrency(progress.filled, "NGN") : formatCurrency(progress.filled)} / {currency === "NGN" ? formatCurrency(progress.target, "NGN") : formatCurrency(progress.target)}
              <span className="text-muted ml-1">({progress.target > 0 ? Math.min(Math.round((progress.filled / progress.target) * 100), 100) : 0}%)</span>
            </span>
          </div>
          <ProgressBar percentage={progress.target > 0 ? Math.min((progress.filled / progress.target) * 100, 100) : 0} />
        </div>
      )}

      {showForm && formType === "naira" && onSubmitNaira && (
        <NairaEntryForm
          onSubmit={onSubmitNaira}
          onSubmitBankStatements={onSubmitNairaBankStatements}
          targetRemaining={progress ? Math.max(0, progress.target - progress.filled) : undefined}
        />
      )}

      {showForm && formType === "tx" && (
        <TxEntryTabs
          direction={direction}
          targetAmount={targetAmount || 0}
          alreadyFilled={alreadyFilled || 0}
          onLinkTx={onSubmitTx}
          onLinkTrades={onSubmitTradeIds}
        />
      )}

      {entries.length > 0 && (
        <div className="rounded-md overflow-hidden mt-2" style={{ border: "1px solid var(--border)" }}>
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="flex items-center justify-between px-3 py-2 text-[13px]"
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-foreground font-medium tabular-nums flex-shrink-0">
                  {currency === "NGN" ? formatCurrency(entry.amount, "NGN") : formatCurrency(entry.amount)}
                </span>
                {entry.tradeId && (
                  <span className="text-[10px] font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded flex-shrink-0">
                    Trade #{entry.tradeId}
                  </span>
                )}
                {entry.sourceLabel && (
                  <span className="text-[11px] text-foreground truncate">{entry.sourceLabel}</span>
                )}
                {entry.reference && (
                  <span className="text-[11px] text-muted font-mono truncate">{truncateHash(entry.reference)}</span>
                )}
                {entry.method && (
                  <span className="text-[10px] text-muted uppercase flex-shrink-0">{entry.method.replace("_", " ")}</span>
                )}
              </div>
              <button
                onClick={() => onUnlink(entry.id)}
                className="text-muted hover:text-danger transition-colors cursor-pointer flex-shrink-0 ml-2"
              >
                <Unlink className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Naira Entry Form (inline)
// =============================================================================

function NairaEntryForm({
  onSubmit,
  onSubmitBankStatements,
  targetRemaining,
}: {
  onSubmit: (data: { amount: number; method: string; reference: string; sourceLabel: string }) => void;
  onSubmitBankStatements?: (statements: { amount: number; reference: string; sourceLabel: string }[]) => void;
  targetRemaining?: number;
}) {
  const [entryMode, setEntryMode] = useState<"bank" | "manual">("bank");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [payerName, setPayerName] = useState("");
  const [payerAccount, setPayerAccount] = useState("");
  const [payerBank, setPayerBank] = useState("");

  // Bank picker state
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);
  const [selectedStmtIds, setSelectedStmtIds] = useState<Set<string>>(new Set());

  const formatAmountInput = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, "");
    return digits ? parseInt(digits).toLocaleString() : "";
  };

  const handleManualSubmit = () => {
    const parsed = parseFloat(amount.replace(/,/g, ""));
    if (!parsed || parsed <= 0) return;
    onSubmit({ amount: parsed, method, reference, sourceLabel: sourceLabel || payerBank });
    setAmount("");
    setReference("");
    setSourceLabel("");
    setPayerName("");
    setPayerAccount("");
    setPayerBank("");
  };

  const handleBankApply = () => {
    if (selectedStmtIds.size === 0 || !onSubmitBankStatements) return;
    const selected = monoBankStatements.filter(s => selectedStmtIds.has(s.id));
    const entries = selected.map(s => {
      const acctLabel = treasuryAccounts.find(a => a.id === s.accountId)?.label || "Unknown";
      return {
        amount: s.amount,
        reference: s.narration.slice(0, 40),
        sourceLabel: acctLabel,
      };
    });
    onSubmitBankStatements(entries);
    setSelectedBankId(null);
    setSelectedStmtIds(new Set());
  };

  return (
    <div className="p-3 rounded-md mb-2 space-y-3" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
      {/* Tab toggle */}
      <div className="flex gap-1 p-0.5 rounded-md bg-surface" role="tablist" style={{ border: "1px solid var(--border)" }}>
        <button
          role="tab"
          aria-selected={entryMode === "bank"}
          onClick={() => setEntryMode("bank")}
          className={`flex-1 text-[11px] font-semibold py-1.5 rounded transition-all cursor-pointer ${entryMode === "bank" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}
        >
          From Bank
        </button>
        <button
          role="tab"
          aria-selected={entryMode === "manual"}
          onClick={() => setEntryMode("manual")}
          className={`flex-1 text-[11px] font-semibold py-1.5 rounded transition-all cursor-pointer ${entryMode === "manual" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}
        >
          Manual Entry
        </button>
      </div>

      {entryMode === "bank" ? (
        /* Phase-based bank picker */
        selectedBankId === null ? (
          <>
            <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Select Bank Account</p>
            <div className="flex flex-wrap gap-2">
              {treasuryAccounts.filter(a => a.type === "bank").map(acct => {
                const availableCount = monoBankStatements.filter(s => s.accountId === acct.id && s.type === "credit" && !s.linkedTradeId).length;
                return (
                  <button
                    key={acct.id}
                    onClick={() => { setSelectedBankId(acct.id); setSelectedStmtIds(new Set()); }}
                    className="flex items-center gap-2 bg-surface hover:bg-surface-3 rounded-lg px-3 py-2 transition-colors cursor-pointer"
                    style={{ border: "1px solid var(--border)" }}
                  >
                    <Building2 className="w-3.5 h-3.5 text-accent" />
                    <div className="text-left">
                      <p className="text-[11px] font-semibold text-foreground">{acct.label}</p>
                      <p className="text-[10px] text-muted">****{acct.accountNumber?.slice(-4)}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${availableCount > 0 ? "bg-success/15 text-success" : "bg-surface-3 text-muted/50"}`}>
                      {availableCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (() => {
          const bankAcct = treasuryAccounts.find(a => a.id === selectedBankId)!;
          const creditStmts = monoBankStatements.filter(s => s.accountId === selectedBankId && s.type === "credit");
          const selectableStmts = creditStmts.filter(s => !s.linkedTradeId);
          const selectedTotal = selectableStmts.filter(s => selectedStmtIds.has(s.id)).reduce((sum, s) => sum + s.amount, 0);
          const remaining = targetRemaining ?? Infinity;
          const isOverpay = selectedTotal > remaining && selectedStmtIds.size > 0;
          const allSelectableSelected = selectableStmts.length > 0 && selectableStmts.every(s => selectedStmtIds.has(s.id));

          return (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button onClick={() => { setSelectedBankId(null); setSelectedStmtIds(new Set()); }} className="inline-flex items-center gap-1 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors cursor-pointer">
                    <ArrowLeft className="w-3 h-3" />Change bank
                  </button>
                  <span className="text-muted/30">|</span>
                  <Building2 className="w-3.5 h-3.5 text-accent" />
                  <p className="text-[11px] font-bold text-foreground">{bankAcct.label}</p>
                </div>
              </div>

              {/* Running total */}
              <div className={`flex items-center justify-between rounded px-2.5 py-1.5 ${isOverpay ? "bg-warning/10" : "bg-surface"}`} style={{ border: `1px solid ${isOverpay ? "var(--warning)" : "var(--border)"}` }}>
                <span className={`text-[11px] font-semibold tabular-nums ${isOverpay ? "text-warning" : "text-foreground"}`}>
                  Selected: ₦{selectedTotal.toLocaleString()}
                </span>
                <span className="text-[10px] text-muted tabular-nums">
                  {selectedStmtIds.size} of {selectableStmts.length} available
                  {remaining < Infinity && ` · ₦${remaining.toLocaleString()} remaining`}
                </span>
              </div>
              {isOverpay && (
                <p className="text-[10px] text-warning font-semibold">
                  Selection exceeds remaining amount by ₦{(selectedTotal - remaining).toLocaleString()}
                </p>
              )}

              {/* Select all */}
              {selectableStmts.length > 0 && (
                <button
                  onClick={() => setSelectedStmtIds(allSelectableSelected ? new Set() : new Set(selectableStmts.map(s => s.id)))}
                  className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors cursor-pointer"
                >
                  <CheckSquare className="w-3 h-3" />{allSelectableSelected ? "Deselect All" : "Select All"}
                </button>
              )}

              {/* Statement list */}
              <div className="max-h-48 overflow-auto space-y-0.5">
                {creditStmts.length === 0 ? (
                  <p className="text-[11px] text-muted py-2">No credit transactions for this account</p>
                ) : (
                  creditStmts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(stmt => {
                    const isLinked = !!stmt.linkedTradeId;
                    const isSelected = selectedStmtIds.has(stmt.id);
                    return (
                      <button
                        key={stmt.id}
                        disabled={isLinked}
                        onClick={() => {
                          const next = new Set(selectedStmtIds);
                          if (next.has(stmt.id)) next.delete(stmt.id);
                          else next.add(stmt.id);
                          setSelectedStmtIds(next);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 transition-colors cursor-pointer ${
                          isLinked ? "opacity-40 cursor-not-allowed" : isSelected ? "bg-accent/10" : "hover:bg-surface"
                        }`}
                      >
                        <span className="flex-shrink-0">
                          {isLinked ? (
                            <CheckSquare className="w-3 h-3 text-muted/30" />
                          ) : isSelected ? (
                            <CheckSquare className="w-3 h-3 text-accent" />
                          ) : (
                            <Square className="w-3 h-3 text-muted/30" />
                          )}
                        </span>
                        <span className="text-[10px] text-muted tabular-nums flex-shrink-0 w-16">{formatDateCompact(stmt.date)}</span>
                        <span className="text-[11px] text-foreground truncate flex-1 min-w-0">{stmt.narration.slice(0, 35)}</span>
                        <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0">₦{stmt.amount.toLocaleString()}</span>
                        {isLinked && <span className="text-[9px] font-bold text-muted bg-surface-3 px-1 py-0.5 rounded flex-shrink-0">Linked</span>}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Apply */}
              <div className="flex justify-end pt-1">
                <Button
                  size="sm"
                  onClick={handleBankApply}
                  disabled={selectedStmtIds.size === 0}
                >
                  Apply ({selectedStmtIds.size} selected{selectedStmtIds.size > 0 ? ` — ₦${selectableStmts.filter(s => selectedStmtIds.has(s.id)).reduce((sum, s) => sum + s.amount, 0).toLocaleString()}` : ""})
                </Button>
              </div>
            </>
          );
        })()
      ) : (
        /* Manual entry */
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-muted block mb-1">Amount (NGN)</label>
              <input
                placeholder="0"
                value={amount}
                onChange={(e) => setAmount(formatAmountInput(e.target.value))}
                className="w-full bg-surface-2 rounded px-2.5 py-1.5 text-[12px] text-foreground tabular-nums focus:outline-none focus:ring-1 focus:ring-accent/30"
                style={{ border: "1px solid var(--border)" }}
              />
            </div>
            <div>
              <label className="text-[11px] text-muted block mb-1">Method</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full bg-surface-2 rounded px-2.5 py-1.5 text-[12px] text-foreground focus:outline-none"
                style={{ border: "1px solid var(--border)" }}
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="p2p">P2P</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] text-muted block mb-1">Bank Reference</label>
            <input
              placeholder="Transfer ref..."
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="w-full bg-surface-2 rounded px-2.5 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
              style={{ border: "1px solid var(--border)" }}
            />
          </div>
          {method === "bank_transfer" && (
            <div className="space-y-2 pt-1">
              <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Payer Details</p>
              <div className="grid grid-cols-2 gap-2">
                <input
                  placeholder="Payer name"
                  value={payerName}
                  onChange={(e) => setPayerName(e.target.value)}
                  className="bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                  style={{ border: "1px solid var(--border)" }}
                />
                <input
                  placeholder="Account number"
                  value={payerAccount}
                  onChange={(e) => setPayerAccount(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  maxLength={10}
                  className="bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                  style={{ border: "1px solid var(--border)" }}
                />
              </div>
              <select
                value={payerBank}
                onChange={(e) => setPayerBank(e.target.value)}
                className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground focus:outline-none"
                style={{ border: "1px solid var(--border)" }}
              >
                <option value="">Select payer bank...</option>
                {NIGERIAN_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end">
            <Button size="sm" onClick={handleManualSubmit}>Add Entry</Button>
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Transaction Multi-Select (inline)
// =============================================================================

function TransactionMultiSelect({
  direction,
  targetAmount,
  alreadyFilled,
  onLink,
}: {
  direction?: "in" | "out";
  targetAmount: number;
  alreadyFilled: number;
  onLink: (ids: string[]) => void;
}) {
  const [txs, setTxs] = useState<DetectedTransaction[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (direction) params.set("direction", direction);
    fetch(`/api/transactions/unlinked?${params}`)
      .then((r) => r.json())
      .then((data) => setTxs(data.transactions || []))
      .finally(() => setLoading(false));
  }, [direction]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedTotal = txs.filter((tx) => selected.has(tx.id)).reduce((sum, tx) => sum + tx.amount, 0);
  const wouldExceed = alreadyFilled + selectedTotal > targetAmount;

  const handleLink = async () => {
    if (selected.size === 0) return;
    setLinking(true);
    onLink(Array.from(selected));
    setLinking(false);
  };

  if (loading) return <p className="text-[12px] text-muted py-3 text-center">Loading transactions...</p>;
  if (txs.length === 0) return <p className="text-[12px] text-muted py-3 text-center">No unlinked transactions available</p>;

  return (
    <div className="mb-2 space-y-2">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setSelected(selected.size === txs.length ? new Set() : new Set(txs.map((t) => t.id)))}
          className="text-[12px] text-accent hover:underline cursor-pointer"
        >
          {selected.size === txs.length ? "Deselect All" : "Select All"}
        </button>
        {selected.size > 0 && (
          <span className="text-[12px] text-foreground tabular-nums">
            {selected.size} selected &mdash; <span className="font-medium">{formatCurrency(selectedTotal)}</span>
          </span>
        )}
      </div>

      {wouldExceed && selected.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: "rgba(251, 191, 36, 0.1)", border: "1px solid rgba(251, 191, 36, 0.2)" }}>
          <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0" />
          <span className="text-[12px] text-warning">
            Exceeds target by {formatCurrency(alreadyFilled + selectedTotal - targetAmount)}
          </span>
        </div>
      )}

      <div className="max-h-56 overflow-y-auto rounded-md" style={{ border: "1px solid var(--border)" }}>
        {txs.map((tx) => (
          <div
            key={tx.id}
            onClick={() => toggle(tx.id)}
            className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors ${selected.has(tx.id) ? "bg-accent/5" : "hover:bg-surface-2/50"}`}
            style={{ borderBottom: "1px solid var(--border)" }}
          >
            {selected.has(tx.id) ? (
              <CheckSquare className="w-4 h-4 text-accent flex-shrink-0" />
            ) : (
              <Square className="w-4 h-4 text-muted flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={`text-[10px] font-bold uppercase ${tx.direction === "incoming" ? "text-accent" : "text-danger"}`}>
                  {tx.direction === "incoming" ? "IN" : "OUT"}
                </span>
                <span className="text-[13px] text-foreground truncate">
                  {tx.senderEntity || truncateHash(tx.counterpartyAddress)}
                </span>
              </div>
              <p className="text-[11px] text-muted">{formatDateTime(tx.timestamp)} · {truncateHash(tx.txHash)}</p>
            </div>
            <span className="text-[13px] font-medium text-foreground tabular-nums whitespace-nowrap">
              {formatCurrency(tx.amount)}
            </span>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={handleLink} disabled={selected.size === 0 || linking}>
          {linking ? "Linking..." : `Link ${selected.size} Transaction${selected.size !== 1 ? "s" : ""}`}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Tx Entry Tabs — From Trades | From Scanner
// =============================================================================

function TxEntryTabs({
  direction,
  targetAmount,
  alreadyFilled,
  onLinkTx,
  onLinkTrades,
}: {
  direction?: "in" | "out";
  targetAmount: number;
  alreadyFilled: number;
  onLinkTx?: (ids: string[]) => void;
  onLinkTrades?: (ids: number[]) => void;
}) {
  const [tab, setTab] = useState<"trades" | "scanner">(onLinkTrades ? "trades" : "scanner");

  return (
    <div className="mb-2 space-y-2">
      <div className="flex gap-1 p-0.5 rounded-md bg-surface" role="tablist" style={{ border: "1px solid var(--border)" }}>
        {onLinkTrades && (
          <button
            role="tab"
            aria-selected={tab === "trades"}
            onClick={() => setTab("trades")}
            className={`flex-1 text-[11px] font-semibold py-1.5 rounded transition-all cursor-pointer ${tab === "trades" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}
          >
            From Trades
          </button>
        )}
        {onLinkTx && (
          <button
            role="tab"
            aria-selected={tab === "scanner"}
            onClick={() => setTab("scanner")}
            className={`flex-1 text-[11px] font-semibold py-1.5 rounded transition-all cursor-pointer ${tab === "scanner" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"}`}
          >
            From Scanner
          </button>
        )}
      </div>

      {tab === "trades" && onLinkTrades && (
        <TradeMultiSelect
          direction={direction}
          targetAmount={targetAmount}
          alreadyFilled={alreadyFilled}
          onLink={onLinkTrades}
        />
      )}

      {tab === "scanner" && onLinkTx && (
        <TransactionMultiSelect
          direction={direction}
          targetAmount={targetAmount}
          alreadyFilled={alreadyFilled}
          onLink={onLinkTx}
        />
      )}
    </div>
  );
}

// =============================================================================
// Trade Multi-Select (from Trade Ledger)
// =============================================================================

function TradeMultiSelect({
  direction,
  targetAmount,
  alreadyFilled,
  onLink,
}: {
  direction?: "in" | "out";
  targetAmount: number;
  alreadyFilled: number;
  onLink: (ids: number[]) => void;
}) {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [linkedTradeIds, setLinkedTradeIds] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [searchQ, setSearchQ] = useState("");

  useEffect(() => {
    setLoading(true);

    // Fetch linked trade IDs and scanner trades in parallel
    Promise.all([
      fetch("/api/locks/linked-trades").then(r => r.ok ? r.json() : { tradeIds: [] }),
      fetch("/api/scanner/transactions").then(r => r.ok ? r.json() : { trades: [] }),
    ]).then(([linkedData, scannerData]) => {
      setLinkedTradeIds(new Set(linkedData.tradeIds || []));

      // Combine mock trades + scanner trades
      const scannerTrades: Trade[] = scannerData.trades || [];
      const allTrades = [...recentTrades, ...scannerTrades];

      const filtered = allTrades.filter(t => {
        if (!direction) return true;
        const isIncoming = t.direction === "buy" || t.direction === "borrow" || t.direction === "return";
        return direction === "in" ? isIncoming : !isIncoming;
      });
      setTrades(filtered);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [direction]);

  const availableTrades = trades.filter(t => !linkedTradeIds.has(t.id));
  const filteredTrades = availableTrades.filter(t => {
    if (!searchQ.trim()) return true;
    const q = searchQ.toLowerCase();
    return (
      (t.counterpartyName?.toLowerCase().includes(q)) ||
      (t.txHash?.toLowerCase().includes(q)) ||
      String(t.usdtAmount).includes(q)
    );
  });

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedTotal = trades.filter(t => selected.has(t.id)).reduce((sum, t) => sum + t.usdtAmount, 0);
  const wouldExceed = alreadyFilled + selectedTotal > targetAmount;

  const handleLink = () => {
    if (selected.size === 0) return;
    setLinking(true);
    onLink(Array.from(selected));
    setLinking(false);
  };

  if (loading) return <p className="text-[12px] text-muted py-3 text-center">Loading trades...</p>;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted" />
        <input
          type="text"
          placeholder="Search trades..."
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          className="w-full bg-surface border border-border rounded-md pl-8 pr-3 py-1.5 text-[12px] text-foreground placeholder:text-muted/60"
        />
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setSelected(selected.size === filteredTrades.length ? new Set() : new Set(filteredTrades.map(t => t.id)))}
          className="text-[12px] text-accent hover:underline cursor-pointer"
        >
          {selected.size === filteredTrades.length && filteredTrades.length > 0 ? "Deselect All" : "Select All"}
        </button>
        {selected.size > 0 && (
          <span className="text-[12px] text-foreground tabular-nums">
            {selected.size} selected — <span className="font-medium">{formatCurrency(selectedTotal)}</span>
          </span>
        )}
      </div>

      {wouldExceed && selected.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: "rgba(251, 191, 36, 0.1)", border: "1px solid rgba(251, 191, 36, 0.2)" }}>
          <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0" />
          <span className="text-[12px] text-warning">
            Exceeds target by {formatCurrency(alreadyFilled + selectedTotal - targetAmount)}
          </span>
        </div>
      )}

      <div className="max-h-56 overflow-y-auto rounded-md" style={{ border: "1px solid var(--border)" }}>
        {filteredTrades.length === 0 ? (
          <p className="text-[12px] text-muted py-4 text-center">No available trades</p>
        ) : (
          filteredTrades.map(t => (
            <div
              key={t.id}
              onClick={() => toggle(t.id)}
              className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors ${selected.has(t.id) ? "bg-accent/5" : "hover:bg-surface-2/50"}`}
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              {selected.has(t.id) ? (
                <CheckSquare className="w-4 h-4 text-accent flex-shrink-0" />
              ) : (
                <Square className="w-4 h-4 text-muted flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] font-bold uppercase ${t.direction === "buy" ? "text-success" : t.direction === "sell" ? "text-danger" : "text-muted"}`}>
                    {t.direction}
                  </span>
                  <span className="text-[13px] text-foreground truncate">
                    {t.counterpartyName || "Unknown"}
                  </span>
                </div>
                <p className="text-[11px] text-muted">
                  Trade #{t.id}
                  {t.txHash && ` · ${truncateHash(t.txHash)}`}
                </p>
              </div>
              <span className="text-[13px] font-medium text-foreground tabular-nums whitespace-nowrap">
                {formatCurrency(t.usdtAmount)}
              </span>
            </div>
          ))
        )}
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={handleLink} disabled={selected.size === 0 || linking}>
          {linking ? "Linking..." : `Link ${selected.size} Trade${selected.size !== 1 ? "s" : ""}`}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Create Lock Modal
// =============================================================================

// =============================================================================
// Create Lock IN Wizard (3-tab: Counterparty → Lock Terms → NGN Funding)
// =============================================================================

const WIZARD_TABS = ["Counterparty", "Lock Terms", "NGN Funding"] as const;
type WizardTab = (typeof WIZARD_TABS)[number];

// Derive current market rate from recent trades (same logic as Trade Ledger)
function getCurrentRate(): number | null {
  const buyTrades = recentTrades.filter((t) => t.direction === "buy" && t.rate);
  if (buyTrades.length === 0) return null;
  return Math.round(buyTrades.reduce((s, t) => s + t.rate!, 0) / buyTrades.length);
}

function CreateLockModal({
  lockType: initialType,
  parentLockId,
  locks,
  onClose,
  onCreated,
}: {
  lockType: "lock_in" | "lock_out";
  parentLockId: number | null;
  locks: LockRecord[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<WizardTab>("Counterparty");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // --- Tab 1: Counterparty ---
  const [selectedCp, setSelectedCp] = useState<Counterparty | null>(null);
  const [cpMode, setCpMode] = useState<"select" | "new">("select");
  const [cpSearch, setCpSearch] = useState("");
  const [newCpName, setNewCpName] = useState("");
  const [newCpPhone, setNewCpPhone] = useState("");
  const [newCpEmail, setNewCpEmail] = useState("");
  const [newCpAddress, setNewCpAddress] = useState("");
  const [showWalletField, setShowWalletField] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // --- Tab 2: Lock Terms ---
  const [lockType, setLockType] = useState(initialType);

  // --- Tab 3: NGN Funding (multi-source) ---
  const [bankSelectedStmtIds, setBankSelectedStmtIds] = useState<Set<string>>(new Set());
  const [manualEntries, setManualEntries] = useState<Array<{ id: string; amount: number; paidToName: string; paidToPhone: string; reference: string }>>(
    []
  );
  const [markAwaitingRemainder, setMarkAwaitingRemainder] = useState(false);
  const [showBankPicker, setShowBankPicker] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [activeBankId, setActiveBankId] = useState<number | null>(null);
  // Temp form state for manual entry inline form
  const [tempManualAmount, setTempManualAmount] = useState("");
  const [tempManualName, setTempManualName] = useState("");
  const [tempManualPhone, setTempManualPhone] = useState("");
  const [tempManualReference, setTempManualReference] = useState("");
  const [tempManualBank, setTempManualBank] = useState("");
  const [usdtAmount, setUsdtAmount] = useState("");
  const [ngnAmount, setNgnAmount] = useState("");
  const [rate, setRate] = useState("");
  const [rateLoading, setRateLoading] = useState(true);

  // Fetch live market rate on mount
  useEffect(() => {
    fetch("/api/scanner/rate")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.buy) {
          setRate(fmtNum(String(data.buy), false));
        } else {
          // API returned null — try mock-data fallback
          const fallback = getCurrentRate();
          setRate(fmtNum(String(fallback || 1374.5), false));
        }
      })
      .catch(() => {
        const fallback = getCurrentRate();
        setRate(fmtNum(String(fallback || 1374.5), false));
      })
      .finally(() => setRateLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [lastEdited, setLastEdited] = useState<"usdt" | "ngn">("usdt");
  const [expiryAt, setExpiryAt] = useState("");
  const [selectedParent, setSelectedParent] = useState<number | null>(parentLockId);
  const [notes, setNotes] = useState("");

  // Format number with thousand separators, max 2 decimal places
  const fmtNum = (val: string, isUserTyping = false) => {
    const stripped = val.replace(/,/g, "");
    if (!stripped) return "";
    const parts = stripped.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    if (parts[1] !== undefined) {
      // When auto-computed, cap at 2 decimals; when typing, let user control
      if (!isUserTyping) parts[1] = parts[1].slice(0, 2);
      // Remove trailing zeros on computed values
      if (!isUserTyping && parts[1]) parts[1] = parts[1].replace(/0+$/, "");
      if (parts[1] === "") return parts[0];
    }
    return parts.join(".");
  };
  // Strip commas to get raw number string
  const rawNum = (val: string) => val.replace(/,/g, "");

  // Bidirectional: type USDT → compute NGN, or type NGN → compute USDT
  // No rounding — show exact decimals
  const parsedRate = parseFloat(rawNum(rate)) || 0;
  const computedNgn = lastEdited === "usdt" && usdtAmount && parsedRate
    ? parseFloat(rawNum(usdtAmount)) * parsedRate
    : ngnAmount ? parseFloat(rawNum(ngnAmount)) || null : null;
  const computedUsdt = lastEdited === "ngn" && ngnAmount && parsedRate
    ? parseFloat(rawNum(ngnAmount)) / parsedRate
    : usdtAmount ? parseFloat(rawNum(usdtAmount)) || null : null;

  const ngnRef = { current: null as HTMLInputElement | null };

  const handleUsdtChange = (val: string) => {
    const clean = rawNum(val);
    const formatted = clean ? fmtNum(clean, true) : "";
    setUsdtAmount(formatted);
    setLastEdited("usdt");
    if (clean && parsedRate) {
      const ngn = parseFloat(clean) * parsedRate;
      setNgnAmount(fmtNum(String(ngn)));
    } else {
      setNgnAmount("");
    }
  };
  const handleNgnChange = (val: string) => {
    const clean = rawNum(val);
    const formatted = clean ? fmtNum(clean, true) : "";
    setNgnAmount(formatted);
    setLastEdited("ngn");
    if (clean && parsedRate) {
      const usdt = parseFloat(clean) / parsedRate;
      setUsdtAmount(fmtNum(String(usdt)));
    } else {
      setUsdtAmount("");
    }
  };
  const handleRateChange = (val: string) => {
    const clean = rawNum(val);
    setRate(clean ? fmtNum(clean, true) : "");
    const r = parseFloat(clean) || 0;
    if (lastEdited === "usdt" && usdtAmount && r) {
      const ngn = parseFloat(rawNum(usdtAmount)) * r;
      setNgnAmount(fmtNum(String(ngn)));
    } else if (lastEdited === "ngn" && ngnAmount && r) {
      const usdt = parseFloat(rawNum(ngnAmount)) / r;
      setUsdtAmount(fmtNum(String(usdt)));
    }
  };
  const counterpartyName = selectedCp ? selectedCp.name : newCpName || null;
  const counterpartyAddress = selectedCp ? "" : newCpAddress;

  // Funding totals (multi-source)
  const bankSelectedTotal = monoBankStatements
    .filter((s) => bankSelectedStmtIds.has(s.id))
    .reduce((sum, s) => sum + s.amount, 0);
  const manualTotal = manualEntries.reduce((sum, e) => sum + e.amount, 0);
  const totalFunded = bankSelectedTotal + manualTotal;
  const awaitingAmount = computedNgn ? Math.max(0, computedNgn - totalFunded) : 0;

  const filteredCps = counterparties.filter((cp) => {
    if (!cpSearch.trim()) return true;
    const q = cpSearch.toLowerCase();
    return cp.name.toLowerCase().includes(q) || (cp.alias?.toLowerCase().includes(q) ?? false);
  });

  // Generate a unique verification token for KYC link
  const verifyToken = `${(selectedCp?.id || "new")}-${Date.now().toString(36)}`;
  const verifyLink = `https://chainfiler.com/verify/${verifyToken}`;
  const handleCopyLink = () => {
    copyText(verifyLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };
  const handleSendEmail = () => {
    if (!newCpEmail.trim()) return;
    // In production this would call an API to send the email
    toast(`Verification link sent to ${newCpEmail}`, "success");
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  // Tab navigation
  const tabIndex = WIZARD_TABS.indexOf(activeTab);
  const canGoNext = () => {
    if (activeTab === "Counterparty") {
      return cpMode === "select" ? !!selectedCp : !!(newCpName.trim() && newCpPhone.trim());
    }
    if (activeTab === "Lock Terms") {
      return !!(parseFloat(rawNum(usdtAmount)) > 0);
    }
    if (activeTab === "NGN Funding") return true; // can always skip (awaiting)
    return true;
  };

  const goNext = () => {
    if (tabIndex < WIZARD_TABS.length - 1) setActiveTab(WIZARD_TABS[tabIndex + 1]);
  };
  const goBack = () => {
    if (tabIndex > 0) setActiveTab(WIZARD_TABS[tabIndex - 1]);
  };

  // Submit
  const handleSubmit = async () => {
    const parsedUsdt = parseFloat(rawNum(usdtAmount));
    if (!parsedUsdt || parsedUsdt <= 0) return setError("Valid USDT amount required");

    // Need at least a name (and phone for new traders)
    const cpAddr = cpMode === "select" ? (selectedCp?.name || "") : newCpAddress.trim();
    const cpName = cpMode === "select" ? selectedCp!.name : newCpName.trim();
    if (!cpName) return setError("Counterparty name required");
    if (cpMode === "new" && !newCpPhone.trim()) return setError("Phone number required for new trader");

    setError("");
    setSaving(true);

    // 1. Create the lock
    const url = parentLockId ? `/api/locks/${parentLockId}/lockouts` : "/api/locks";
    const payload: Record<string, unknown> = {
      counterpartyAddress: cpAddr,
      counterpartyName: cpName,
      counterpartyPhone: cpMode === "new" ? newCpPhone.trim() : null,
      counterpartyEmail: cpMode === "new" && newCpEmail.trim() ? newCpEmail.trim() : null,
      verifyToken: cpMode === "new" ? verifyToken : null,
      usdtAmount: parsedUsdt,
      rate: rate ? parseFloat(rawNum(rate)) : null,
      expiryAt: expiryAt || null,
      notes: notes.trim() || null,
    };
    if (!parentLockId) {
      payload.lockType = lockType;
      if (lockType === "lock_out" && selectedParent) payload.parentLockId = selectedParent;
    }

    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      setSaving(false);
      const data = await res.json();
      return setError(data.error || "Failed to create lock");
    }

    const lockData = await res.json();
    const lockId = lockData.id;

    // 2. Create NGN funding entries (multi-source)
    const entryType = lockType === "lock_in" ? "naira_funding" : "naira_paid";

    // Bank statement entries
    if (bankSelectedStmtIds.size > 0) {
      const selected = monoBankStatements.filter((s) => bankSelectedStmtIds.has(s.id));
      for (const s of selected) {
        const acctLabel = treasuryAccounts.find((a) => a.id === s.accountId)?.label || "Unknown";
        await fetch(`/api/locks/${lockId}/entries`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            entryType,
            amount: s.amount,
            method: "bank_transfer",
            reference: s.narration.slice(0, 40),
            sourceLabel: acctLabel,
          }),
        });
      }
    }

    // Manual entries
    for (const me of manualEntries) {
      await fetch(`/api/locks/${lockId}/entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entryType,
          amount: me.amount,
          method: "manual",
          reference: me.reference || null,
          sourceLabel: null,
          paidToName: me.paidToName || null,
          paidToPhone: me.paidToPhone || null,
        }),
      });
    }
    // If markAwaitingRemainder is checked, no additional entry needed — lock stays open/partial

    setSaving(false);
    toast("Lock created successfully", "success");
    if (lockData.emailResult) {
      toast(lockData.emailResult.message, "info");
    }
    onCreated();
  };

  return (
    <ModalOverlay onClose={onClose} title={parentLockId ? "New Lock OUT" : "New Lock IN"}>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* Step indicators */}
        <div className="flex items-center gap-1" role="tablist">
          {WIZARD_TABS.map((t, i) => {
            const isActive = t === activeTab;
            const isPast = i < tabIndex;
            return (
              <button
                key={t}
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  // Only allow going back or to current
                  if (i <= tabIndex) setActiveTab(t);
                }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-colors cursor-pointer ${
                  isActive
                    ? "btn-pill-active text-accent"
                    : isPast
                    ? "text-accent/60 hover:text-accent"
                    : "text-muted/40"
                }`}
              >
                <span className={`w-4.5 h-4.5 rounded-full text-[10px] flex items-center justify-center font-bold ${
                  isActive ? "bg-accent text-[#050506]" : isPast ? "bg-accent/20 text-accent" : "bg-surface-3 text-muted/50"
                }`}>
                  {isPast ? "✓" : i + 1}
                </span>
                {t}
                {i < WIZARD_TABS.length - 1 && <ChevronRight className="w-3 h-3 text-muted/30 ml-1" />}
              </button>
            );
          })}
        </div>

        {/* ============ TAB 1: Counterparty ============ */}
        {activeTab === "Counterparty" && (
          <div className="space-y-3">
            {/* Mode toggle */}
            <div className="flex gap-1 p-0.5 rounded-md bg-surface" style={{ border: "1px solid var(--border)" }}>
              <button
                onClick={() => setCpMode("select")}
                className={`flex-1 text-[11px] font-semibold py-1.5 rounded transition-colors cursor-pointer ${
                  cpMode === "select" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"
                }`}
              >
                Existing Counterparty
              </button>
              <button
                onClick={() => setCpMode("new")}
                className={`flex-1 text-[11px] font-semibold py-1.5 rounded transition-colors cursor-pointer ${
                  cpMode === "new" ? "btn-pill-active text-accent" : "text-muted hover:text-foreground"
                }`}
              >
                <UserPlus className="w-3 h-3 inline mr-1" />
                New Counterparty
              </button>
            </div>

            {cpMode === "select" ? (
              <>
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted" />
                  <input
                    type="text"
                    placeholder="Search counterparties..."
                    value={cpSearch}
                    onChange={(e) => setCpSearch(e.target.value)}
                    className="w-full bg-surface border border-border rounded-md pl-8 pr-3 py-1.5 text-[12px] text-foreground placeholder:text-muted/60"
                  />
                </div>

                {/* Counterparty list */}
                <div className="max-h-52 overflow-y-auto rounded-md" style={{ border: "1px solid var(--border)" }}>
                  {filteredCps.length === 0 ? (
                    <p className="text-[12px] text-muted py-4 text-center">No counterparties found</p>
                  ) : (
                    filteredCps.map((cp) => {
                      const isSelected = selectedCp?.id === cp.id;
                      return (
                        <div
                          key={cp.id}
                          onClick={() => setSelectedCp(isSelected ? null : cp)}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                            isSelected ? "bg-accent/10" : "hover:bg-surface-2/50"
                          }`}
                          style={{ borderBottom: "1px solid var(--border)" }}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-4 h-4 text-accent flex-shrink-0" />
                          ) : (
                            <Square className="w-4 h-4 text-muted flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[13px] text-foreground font-medium">{cp.name}</span>
                              {cp.alias && (
                                <span className="text-[11px] text-muted">({cp.alias})</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] text-muted">{cp.city}</span>
                              <span className="text-[10px] text-muted">·</span>
                              <span className="text-[10px] text-muted">{cp.tradeCount} trades</span>
                              <span className="text-[10px] text-muted">·</span>
                              <span className={`text-[10px] font-semibold ${cp.kycVerified ? "text-accent" : "text-warning"}`}>
                                {cp.kycVerified ? "KYC Verified" : "Unverified"}
                              </span>
                            </div>
                          </div>
                          <span className="text-[11px] text-muted tabular-nums">{formatCurrency(cp.totalVolumeUsd)}</span>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Selected CP summary */}
                {selectedCp && !selectedCp.kycVerified && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: "rgba(251, 191, 36, 0.1)", border: "1px solid rgba(251, 191, 36, 0.2)" }}>
                    <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0" />
                    <span className="text-[11px] text-warning flex-1">
                      {selectedCp.name} is not KYC verified.
                    </span>
                    <button
                      onClick={handleCopyLink}
                      className="text-[10px] font-semibold text-accent hover:underline cursor-pointer flex items-center gap-1"
                    >
                      {copiedLink ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copiedLink ? "Copied!" : "Copy verify link"}
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* New trader quick-add form */
              <div className="space-y-2.5 p-3 rounded-md" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[11px] text-muted block mb-1">Name *</label>
                    <input
                      placeholder="Company or individual"
                      value={newCpName}
                      onChange={(e) => setNewCpName(e.target.value)}
                      className="w-full bg-surface border border-border rounded-md px-2.5 py-1.5 text-[13px] text-foreground"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted block mb-1">Phone *</label>
                    <input
                      placeholder="+234 ..."
                      value={newCpPhone}
                      onChange={(e) => setNewCpPhone(e.target.value)}
                      className="w-full bg-surface border border-border rounded-md px-2.5 py-1.5 text-[13px] text-foreground"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] text-muted block mb-1">Email (optional)</label>
                  <div className="flex gap-2">
                    <input
                      placeholder="trader@company.com"
                      value={newCpEmail}
                      onChange={(e) => { setNewCpEmail(e.target.value); setEmailSent(false); }}
                      className="flex-1 bg-surface border border-border rounded-md px-2.5 py-1.5 text-[13px] text-foreground"
                    />
                    {newCpEmail.trim() && (
                      <button
                        onClick={handleSendEmail}
                        disabled={emailSent}
                        className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors cursor-pointer ${
                          emailSent
                            ? "btn-pill-active text-accent"
                            : "bg-accent/10 text-accent hover:bg-accent/20"
                        }`}
                      >
                        {emailSent ? "Sent" : "Send KYC link"}
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-muted mt-0.5">
                    We&apos;ll send a KYC verification link to this email
                  </p>
                </div>

                {/* Wallet address — collapsible */}
                {!showWalletField ? (
                  <button
                    onClick={() => setShowWalletField(true)}
                    className="text-[11px] text-accent hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add wallet address
                  </button>
                ) : (
                  <div>
                    <label className="text-[11px] text-muted block mb-1">Wallet Address</label>
                    <input
                      placeholder="TRC-20 / BEP-20 / ETH address..."
                      value={newCpAddress}
                      onChange={(e) => setNewCpAddress(e.target.value)}
                      className="w-full bg-surface border border-border rounded-md px-2.5 py-1.5 text-[12px] text-foreground font-mono"
                    />
                    <p className="text-[10px] text-muted mt-0.5">
                      Auto-tags this address in the Trade Ledger for future identification
                    </p>
                  </div>
                )}

                {/* Verification link — always visible */}
                <div className="mt-1 p-2.5 rounded-md bg-surface space-y-2" style={{ border: "1px solid var(--border)" }}>
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-foreground font-medium">KYC Verification Link</p>
                      <p className="text-[10px] text-muted truncate">{verifyLink}</p>
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className="text-[10px] font-semibold text-accent hover:underline cursor-pointer flex items-center gap-1 whitespace-nowrap"
                    >
                      {copiedLink ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copiedLink ? "Copied!" : "Copy link"}
                    </button>
                  </div>
                  <p className="text-[10px] text-muted leading-relaxed">
                    When they complete the form via this link, their KYC will be linked to this lock automatically.
                    {newCpPhone.trim() && " If they already use ChainFiler, the system will request KYC share from their account."}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============ TAB 2: NGN Funding (multi-source) ============ */}
        {activeTab === "NGN Funding" && (
          <div className="space-y-3">
            {/* Review card — counterparty / USDT / rate */}
            <div className="p-2.5 rounded-md" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-[11px] min-w-0">
                  <span className="text-foreground font-medium truncate">
                    {cpMode === "select" ? selectedCp?.name || "—" : newCpName || "—"}
                  </span>
                  <span className="text-muted">·</span>
                  <span className="text-foreground font-semibold tabular-nums whitespace-nowrap">
                    {usdtAmount ? formatCurrency(parseFloat(rawNum(usdtAmount))) + " USDT" : "—"}
                  </span>
                  {rate && <span className="text-muted tabular-nums whitespace-nowrap">@ ₦{parseFloat(rawNum(rate)).toLocaleString()}</span>}
                </div>
                {computedNgn && (
                  <span className="text-[13px] font-bold text-foreground tabular-nums whitespace-nowrap">
                    ₦{computedNgn.toLocaleString()}
                  </span>
                )}
              </div>
              {expiryAt && (
                <p className="text-[10px] text-muted mt-1">Deadline: {formatDateTime(expiryAt)}</p>
              )}
            </div>

            {/* Progress bar against target */}
            {computedNgn ? (() => {
              const pct = Math.min((totalFunded / computedNgn) * 100, 100);
              const isOver = totalFunded > computedNgn;
              const isMatch = totalFunded === computedNgn;
              return (
                <div className="rounded-md bg-surface overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                  <div className="flex items-center justify-between px-2.5 py-1.5">
                    <span className="text-[11px] font-semibold text-foreground">
                      Target: ₦{computedNgn.toLocaleString()}
                    </span>
                    <span className={`text-[10px] font-semibold tabular-nums ${
                      isMatch ? "text-success" : isOver ? "text-warning" : "text-muted"
                    }`}>
                      {totalFunded > 0
                        ? `₦${totalFunded.toLocaleString()} — ${Math.round(pct)}% funded`
                        : "No funding yet"}
                    </span>
                  </div>
                  <div className="h-1.5 bg-surface-3">
                    <div
                      className={`h-full transition-all duration-300 ${
                        isOver ? "bg-warning" : isMatch ? "bg-success" : "bg-accent"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })() : null}

            {/* Entry list — all bank + manual entries */}
            {(bankSelectedStmtIds.size > 0 || manualEntries.length > 0) ? (
              <div className="space-y-0.5">
                {/* Bank statement entries */}
                {monoBankStatements
                  .filter((s) => bankSelectedStmtIds.has(s.id))
                  .map((stmt) => {
                    const acctLabel = treasuryAccounts.find((a) => a.id === stmt.accountId)?.label || "Unknown";
                    const acctNum = treasuryAccounts.find((a) => a.id === stmt.accountId)?.accountNumber;
                    return (
                      <div
                        key={stmt.id}
                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-accent/5"
                        style={{ border: "1px solid rgba(99,102,241,0.1)" }}
                      >
                        <CheckSquare className="w-3 h-3 text-accent flex-shrink-0" />
                        <span className="text-[11px] text-foreground truncate flex-1 min-w-0">
                          {acctLabel} {acctNum ? `****${acctNum.slice(-4)}` : ""}
                        </span>
                        <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0">
                          ₦{stmt.amount.toLocaleString()}
                        </span>
                        <button
                          onClick={() => {
                            const next = new Set(bankSelectedStmtIds);
                            next.delete(stmt.id);
                            setBankSelectedStmtIds(next);
                          }}
                          className="text-muted hover:text-danger transition-colors cursor-pointer flex-shrink-0"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}

                {/* Manual entries */}
                {manualEntries.map((me) => (
                  <div
                    key={me.id}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-accent/5"
                    style={{ border: "1px solid rgba(99,102,241,0.1)" }}
                  >
                    <CheckSquare className="w-3 h-3 text-accent flex-shrink-0" />
                    <span className="text-[11px] text-foreground truncate flex-1 min-w-0">
                      Manual: {me.paidToName || "—"}{me.paidToPhone ? ` (${me.paidToPhone.slice(0, 7)}...)` : ""}
                    </span>
                    <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0">
                      ₦{me.amount.toLocaleString()}
                    </span>
                    <button
                      onClick={() => setManualEntries((prev) => prev.filter((e) => e.id !== me.id))}
                      className="text-muted hover:text-danger transition-colors cursor-pointer flex-shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : !showBankPicker && !showManualForm ? (
              /* Empty state */
              <div className="flex flex-col items-center py-4 text-center">
                <Clock className="w-6 h-6 text-muted/40 mb-1.5" />
                <p className="text-[12px] text-muted font-medium">No funding added yet</p>
                <p className="text-[10px] text-muted/60 mt-0.5">Use the buttons below to add funding sources</p>
              </div>
            ) : null}

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => { setShowBankPicker(!showBankPicker); setShowManualForm(false); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors cursor-pointer ${
                  showBankPicker ? "btn-pill-active text-accent" : "bg-surface hover:bg-surface-3 text-foreground"
                }`}
                style={{ border: "1px solid var(--border)" }}
              >
                <Plus className="w-3 h-3" /> From Bank
              </button>
              <button
                onClick={() => { setShowManualForm(!showManualForm); setShowBankPicker(false); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors cursor-pointer ${
                  showManualForm ? "btn-pill-active text-accent" : "bg-surface hover:bg-surface-3 text-foreground"
                }`}
                style={{ border: "1px solid var(--border)" }}
              >
                <Plus className="w-3 h-3" /> Manual Entry
              </button>
            </div>

            {/* Inline bank picker */}
            {showBankPicker && (
              <div className="rounded-md p-3 space-y-2.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold text-muted uppercase tracking-wider">
                    {activeBankId === null ? "Select Bank Account" : (() => {
                      const bankAcct = treasuryAccounts.find((a) => a.id === activeBankId);
                      return bankAcct ? bankAcct.label : "Bank";
                    })()}
                  </p>
                  <button
                    onClick={() => { setShowBankPicker(false); setActiveBankId(null); }}
                    className="text-muted hover:text-foreground transition-colors cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {activeBankId === null ? (
                  /* Phase 1: Bank selection */
                  <div className="flex flex-wrap gap-2">
                    {treasuryAccounts.filter((a) => a.type === "bank").map((acct) => {
                      const availableCount = monoBankStatements.filter(
                        (s) => s.accountId === acct.id && s.type === "credit" && !s.linkedTradeId && !bankSelectedStmtIds.has(s.id)
                      ).length;
                      return (
                        <button
                          key={acct.id}
                          onClick={() => setActiveBankId(acct.id)}
                          className="flex items-center gap-2 bg-surface hover:bg-surface-3 rounded-lg px-3 py-2 transition-colors cursor-pointer"
                          style={{ border: "1px solid var(--border)" }}
                        >
                          <Building2 className="w-3.5 h-3.5 text-accent" />
                          <div className="text-left">
                            <p className="text-[11px] font-semibold text-foreground">{acct.label}</p>
                            <p className="text-[10px] text-muted">****{acct.accountNumber?.slice(-4)}</p>
                          </div>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            availableCount > 0 ? "bg-success/15 text-success" : "bg-surface-3 text-muted/50"
                          }`}>
                            {availableCount}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (() => {
                  /* Phase 2: Statement list for selected bank */
                  const creditStmts = monoBankStatements.filter(
                    (s) => s.accountId === activeBankId && s.type === "credit"
                  );
                  const selectableStmts = creditStmts.filter((s) => !s.linkedTradeId);
                  const bankLocalSelected = selectableStmts.filter((s) => bankSelectedStmtIds.has(s.id));
                  const allSelectableSelected =
                    selectableStmts.length > 0 && selectableStmts.every((s) => bankSelectedStmtIds.has(s.id));

                  return (
                    <>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setActiveBankId(null)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors cursor-pointer"
                        >
                          <ArrowLeft className="w-3 h-3" />Change bank
                        </button>
                      </div>

                      {/* Select all toggle */}
                      {selectableStmts.length > 0 && (
                        <button
                          onClick={() => {
                            const next = new Set(bankSelectedStmtIds);
                            if (allSelectableSelected) {
                              selectableStmts.forEach((s) => next.delete(s.id));
                            } else {
                              selectableStmts.forEach((s) => next.add(s.id));
                            }
                            setBankSelectedStmtIds(next);
                          }}
                          className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-accent hover:text-accent-hover transition-colors cursor-pointer"
                        >
                          <CheckSquare className="w-3 h-3" />
                          {allSelectableSelected ? "Deselect All" : "Select All"}
                        </button>
                      )}

                      {/* Statement list */}
                      <div className="max-h-48 overflow-auto space-y-0.5">
                        {creditStmts.length === 0 ? (
                          <p className="text-[11px] text-muted py-2">No credit transactions for this account</p>
                        ) : (
                          creditStmts
                            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                            .map((stmt) => {
                              const isLinked = !!stmt.linkedTradeId;
                              const isSelected = bankSelectedStmtIds.has(stmt.id);
                              return (
                                <button
                                  key={stmt.id}
                                  disabled={isLinked}
                                  onClick={() => {
                                    const next = new Set(bankSelectedStmtIds);
                                    if (next.has(stmt.id)) next.delete(stmt.id);
                                    else next.add(stmt.id);
                                    setBankSelectedStmtIds(next);
                                  }}
                                  className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 transition-colors cursor-pointer ${
                                    isLinked
                                      ? "opacity-40 cursor-not-allowed"
                                      : isSelected
                                      ? "bg-accent/10"
                                      : "hover:bg-surface"
                                  }`}
                                >
                                  <span className="flex-shrink-0">
                                    {isLinked ? (
                                      <CheckSquare className="w-3 h-3 text-muted/30" />
                                    ) : isSelected ? (
                                      <CheckSquare className="w-3 h-3 text-accent" />
                                    ) : (
                                      <Square className="w-3 h-3 text-muted/30" />
                                    )}
                                  </span>
                                  <span className="text-[10px] text-muted tabular-nums flex-shrink-0 w-16">
                                    {formatDateCompact(stmt.date)}
                                  </span>
                                  <span className="text-[11px] text-foreground truncate flex-1 min-w-0">
                                    {stmt.narration.slice(0, 35)}
                                  </span>
                                  <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0">
                                    ₦{stmt.amount.toLocaleString()}
                                  </span>
                                  {isLinked && (
                                    <span className="text-[9px] font-bold text-muted bg-surface-3 px-1 py-0.5 rounded flex-shrink-0">
                                      Linked
                                    </span>
                                  )}
                                </button>
                              );
                            })
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Inline manual entry form */}
            {showManualForm && (
              <div className="rounded-md p-3 space-y-2.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Add Manual Entry</p>
                  <button
                    onClick={() => setShowManualForm(false)}
                    className="text-muted hover:text-foreground transition-colors cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-muted block mb-1">Amount (NGN) *</label>
                    <input
                      placeholder="0"
                      value={tempManualAmount}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/[^0-9]/g, "");
                        setTempManualAmount(digits ? parseInt(digits).toLocaleString() : "");
                      }}
                      className="w-full bg-surface-2 rounded px-2.5 py-1.5 text-[12px] text-foreground tabular-nums focus:outline-none focus:ring-1 focus:ring-accent/30"
                      style={{ border: "1px solid var(--border)" }}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted block mb-1">Paid To Name</label>
                    <input
                      placeholder="e.g. Adamu Ibrahim"
                      value={tempManualName}
                      onChange={(e) => setTempManualName(e.target.value)}
                      className="w-full bg-surface-2 rounded px-2.5 py-1.5 text-[12px] text-foreground placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                      style={{ border: "1px solid var(--border)" }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    placeholder="Account number"
                    value={tempManualPhone}
                    onChange={(e) => setTempManualPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    maxLength={10}
                    className="bg-surface-2 rounded px-2.5 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                    style={{ border: "1px solid var(--border)" }}
                  />
                  <input
                    placeholder="Transfer ref..."
                    value={tempManualReference}
                    onChange={(e) => setTempManualReference(e.target.value)}
                    className="bg-surface-2 rounded px-2.5 py-1.5 text-[12px] text-foreground font-mono placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent/30"
                    style={{ border: "1px solid var(--border)" }}
                  />
                </div>
                <select
                  value={tempManualBank ?? ""}
                  onChange={(e) => setTempManualBank(e.target.value)}
                  className="w-full bg-surface-2 rounded px-2 py-1.5 text-[12px] text-foreground focus:outline-none"
                  style={{ border: "1px solid var(--border)" }}
                >
                  <option value="">Select bank...</option>
                  {NIGERIAN_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => {
                      const amt = parseFloat(tempManualAmount.replace(/,/g, ""));
                      if (!amt || amt <= 0) return;
                      setManualEntries((prev) => [
                        ...prev,
                        {
                          id: `manual_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                          amount: amt,
                          paidToName: tempManualName,
                          paidToPhone: tempManualPhone,
                          reference: tempManualReference,
                        },
                      ]);
                      setTempManualAmount("");
                      setTempManualName("");
                      setTempManualPhone("");
                      setTempManualReference("");
                      setTempManualBank("");
                    }}
                    disabled={!tempManualAmount || (parseFloat(tempManualAmount.replace(/,/g, "")) || 0) <= 0}
                  >
                    Add Entry
                  </Button>
                </div>
              </div>
            )}

            {/* Awaiting remainder checkbox */}
            {computedNgn && totalFunded < computedNgn ? (
              <button
                onClick={() => setMarkAwaitingRemainder(!markAwaitingRemainder)}
                className="flex items-center gap-2 w-full px-2.5 py-2 rounded-md hover:bg-surface-2/50 transition-colors cursor-pointer text-left"
                style={{ border: "1px solid var(--border)" }}
              >
                {markAwaitingRemainder ? (
                  <CheckSquare className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                ) : (
                  <Square className="w-3.5 h-3.5 text-muted flex-shrink-0" />
                )}
                <span className="text-[11px] text-foreground">
                  Mark remaining ₦{awaitingAmount.toLocaleString()} as Awaiting NGN
                </span>
              </button>
            ) : null}
          </div>
        )}

        {/* ============ TAB 2: Lock Terms ============ */}
        {activeTab === "Lock Terms" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-muted block mb-1">USDT Amount</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={usdtAmount}
                  onChange={(e) => handleUsdtChange(e.target.value)}
                  className="w-full bg-surface border border-border rounded-md px-2.5 py-1.5 text-[13px] text-foreground tabular-nums"
                />
              </div>
              <div>
                <label className="text-[11px] text-muted block mb-1">Rate (NGN/USDT)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Auto-filled"
                  value={rate}
                  onChange={(e) => handleRateChange(e.target.value)}
                  className="w-full bg-surface border border-border rounded-md px-2.5 py-1.5 text-[13px] text-foreground tabular-nums"
                />
              </div>
            </div>

            <div
              onClick={() => ngnRef.current?.focus()}
              className="flex items-center justify-between px-3 py-2.5 rounded-md bg-accent/5 cursor-text"
              style={{ border: "1px solid rgba(99,102,241,0.15)" }}
            >
              <span className="text-[11px] text-muted">NGN Amount</span>
              <div className="flex items-center gap-1">
                <span className="text-[14px] font-bold text-foreground/40">₦</span>
                <input
                  ref={(el) => { ngnRef.current = el; }}
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={ngnAmount}
                  onChange={(e) => handleNgnChange(e.target.value)}
                  className="bg-transparent text-right text-[14px] font-bold text-foreground tabular-nums w-44 outline-none placeholder:text-muted/40"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] text-muted block mb-1">Delivery Deadline</label>
              <input
                type="datetime-local"
                value={expiryAt}
                onChange={(e) => setExpiryAt(e.target.value)}
                className="w-full bg-surface border border-border rounded-md px-2.5 py-1.5 text-[13px] text-foreground"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted block mb-1">Notes (optional)</label>
              <input
                placeholder="Lock notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-surface border border-border rounded-md px-2.5 py-1.5 text-[13px] text-foreground"
              />
            </div>
          </div>
        )}

        {/* Error */}
        {error && <p className="text-[12px] text-danger">{error}</p>}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <div>
            {tabIndex > 0 && (
              <Button variant="ghost" size="sm" onClick={goBack}>
                <ArrowLeft className="w-3 h-3" /> Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            {tabIndex < WIZARD_TABS.length - 1 ? (
              <Button size="sm" onClick={goNext} disabled={!canGoNext()}>
                Next <ChevronRight className="w-3 h-3" />
              </Button>
            ) : (
              <Button size="sm" onClick={handleSubmit} disabled={saving || !usdtAmount}>
                {saving ? "Creating..." : "Create Lock"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

// =============================================================================
// Shared Modal Overlay
// =============================================================================

function ModalOverlay({
  onClose,
  title,
  children,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 8 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="pointer-events-auto w-full max-w-xl rounded-lg overflow-hidden"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
          }}
        >
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
            <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
            <button onClick={onClose} aria-label="Close" className="text-muted hover:text-foreground transition-colors cursor-pointer">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 py-4">{children}</div>
        </div>
      </motion.div>
    </>
  );
}
